import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { I18nextProvider } from 'react-i18next'
import i18n from '@/modules/utils/i18n'
import { ApolloProvider } from '@apollo/client'
import client from '@/modules/apolloClient'
import { AuthProvider } from '@/modules/context/AuthContext'
import { createRoot, Root } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import React, { JSX, StrictMode } from 'react'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact, { BugsnagErrorBoundary, BugsnagPluginReactResult } from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'
import { NotificationProvider } from '@/modules/context/NotificationContext.tsx'
import MuiThemeProvider from './MuiThemeProvider'

const container: HTMLElement | null = document.getElementById('root')
const root: Root = createRoot(container!)

const renderApp: () => void = (): void => {
    const AppComponent: JSX.Element = (
        <ApolloProvider client={client} key={i18n.language}>
            <I18nextProvider i18n={i18n}>
                <MuiThemeProvider>
                    <BrowserRouter>
                        <AuthProvider>
                            <NotificationProvider>
                                <App />
                            </NotificationProvider>
                        </AuthProvider>
                    </BrowserRouter>
                </MuiThemeProvider>
            </I18nextProvider>
        </ApolloProvider>
    )

    const bugsnagKey: string | undefined = import.meta.env.VITE_BUGSNAG_KEY

    if (bugsnagKey) {
        Bugsnag.start({
            apiKey: bugsnagKey,
            plugins: [new BugsnagPluginReact()],
            releaseStage: import.meta.env.VITE_RELEASE_STAGE,
            appVersion: __APP_VERSION__
        })

        BugsnagPerformance.start({ apiKey: bugsnagKey })

        const errorBoundaryPlugin: BugsnagPluginReactResult | undefined = Bugsnag.getPlugin('react')
        if (errorBoundaryPlugin) {
            const ErrorBoundary: BugsnagErrorBoundary = errorBoundaryPlugin.createErrorBoundary(React)
            root.render(
                <ErrorBoundary>
                    <StrictMode>{AppComponent}</StrictMode>
                </ErrorBoundary>
            )
        } else {
            root.render(<StrictMode>{AppComponent}</StrictMode>)
        }
    } else {
        root.render(<StrictMode>{AppComponent}</StrictMode>)
    }
}

renderApp()

serviceWorkerRegistration.unregister()
